import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Users, Heart, AlertTriangle, ShoppingBag } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function RecipeDetail({ recipe, isFavorite, onToggleFavorite, onClose }) {
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list('-created_date'),
  });

  // Match products with recipe ingredients
  const suggestedProducts = products.filter(product => {
    return recipe.ingredients?.some(ingredient => {
      const ingLower = ingredient.toLowerCase();
      const nameLower = product.name?.toLowerCase() || '';
      const brandLower = product.brand?.toLowerCase() || '';
      return nameLower.includes(ingLower) || brandLower.includes(ingLower);
    });
  }).slice(0, 5);

  // Calculate nutrition alerts
  const nutritionAlerts = [];
  if (recipe.nutrition_info) {
    if (recipe.nutrition_info.calories > 500) {
      nutritionAlerts.push('Alta en calorías');
    }
    if (recipe.nutrition_info.carbs > 50) {
      nutritionAlerts.push('Alta en carbohidratos');
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="relative">
            {recipe.image_url ? (
              <div className="h-48 overflow-hidden">
                <img src={recipe.image_url} alt={recipe.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
            ) : (
              <div className="h-48 bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                <span className="text-8xl">🍳</span>
              </div>
            )}

            <div className="absolute top-4 right-4 flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleFavorite}
                className="rounded-full bg-white/90 backdrop-blur-sm hover:bg-white"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
              </Button>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{recipe.title}</h2>
            {recipe.description && (
              <p className="text-gray-600 mb-4">{recipe.description}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              {recipe.prep_time && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{recipe.prep_time} min</span>
                </div>
              )}
              {recipe.servings && (
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{recipe.servings} porciones</span>
                </div>
              )}
            </div>

            {recipe.health_tags && recipe.health_tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {recipe.health_tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Nutrition Alerts */}
            {nutritionAlerts.length > 0 && (
              <Card className="p-4 bg-amber-50 border-amber-200 mb-6">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900 mb-1">Ten en cuenta</p>
                    <ul className="space-y-1">
                      {nutritionAlerts.map((alert, index) => (
                        <li key={index} className="text-sm text-amber-800">• {alert}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            )}

            {/* Ingredients */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Ingredientes</h3>
              <ul className="space-y-2">
                {recipe.ingredients?.map((ingredient, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{ingredient}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Suggested Products */}
            {suggestedProducts.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingBag className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Productos sugeridos</h3>
                </div>
                <div className="space-y-2">
                  {suggestedProducts.map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-200" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                        {product.brand && <p className="text-xs text-gray-500">{product.brand}</p>}
                      </div>
                      {product.scaneat_score !== undefined && (
                        <div className={`
                          w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm
                          ${product.traffic_light === 'green' ? 'bg-emerald-50 text-emerald-700' :
                            product.traffic_light === 'yellow' ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'}
                        `}>
                          {product.scaneat_score}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Preparación</h3>
              <p className="text-gray-700 whitespace-pre-line">{recipe.instructions}</p>
            </div>

            {/* Nutrition Info */}
            {recipe.nutrition_info && (
              <Card className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Información nutricional (por porción)</h3>
                <div className="grid grid-cols-2 gap-3">
                  {recipe.nutrition_info.calories && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-xl font-bold text-gray-900">{recipe.nutrition_info.calories}</p>
                      <p className="text-xs text-gray-500">Calorías</p>
                    </div>
                  )}
                  {recipe.nutrition_info.protein && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-xl font-bold text-gray-900">{recipe.nutrition_info.protein}g</p>
                      <p className="text-xs text-gray-500">Proteínas</p>
                    </div>
                  )}
                  {recipe.nutrition_info.carbs && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-xl font-bold text-gray-900">{recipe.nutrition_info.carbs}g</p>
                      <p className="text-xs text-gray-500">Carbohidratos</p>
                    </div>
                  )}
                  {recipe.nutrition_info.fats && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-xl font-bold text-gray-900">{recipe.nutrition_info.fats}g</p>
                      <p className="text-xs text-gray-500">Grasas</p>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}